import { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_ACCEPT_STATUS_NONE,
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_PENDING,
} from '@rsos/constants/rcsStatuses';
import { useFlag } from '@rsos/flags';
import { useGetRcsConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { useChatContext } from '../ChatContext';
import { OutgoingTranslationWarning } from '../SharedChatUI/OutgoingTranslationWarning';
import RcsChat from './RcsChat';
import { RcsChatContainer } from './RcsChat.styles';
import RcsChatHeader from './RcsChatHeader';
import RcsChatNext from './RcsChatNext';
import {
  RCS_CHAT_HEADER_HEIGHT,
  CHAT_HEADER_END,
  CHAT_HEADER_ACCEPT_DECLINE,
} from './constants';

export const ICON_ROW_HEIGHT = 36;

const RcsChatBox = () => {
  const { isCollapsed } = useChatContext();
  const [showPreconfiguredMessages, setShowPreconfiguredMessages] =
    useState(false);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const hasTalkJSImprovements = useFlag('talkjs_improvements');

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  useGetRcsConversationMetadataQuery(
    {
      orgName,
      callerID: selectedCallerID,
    },
    {
      skip: !selectedCallerID || rcsConversation,
    },
  );

  const handleClickShowAccordion = useCallback(() => {
    setShowPreconfiguredMessages(prev => !prev);
  }, []);

  const showButtons = useMemo(() => {
    const STATUS_TO_BUTTON_MAP = {
      [`${RCS_ACCEPT_STATUS_NONE}${RCS_CONVERSATION_STATUS_PENDING}`]:
        CHAT_HEADER_ACCEPT_DECLINE,
      [`${RCS_ACCEPT_STATUS_ACCEPTED}${RCS_CONVERSATION_STATUS_ACTIVE}`]:
        CHAT_HEADER_END,
      [`${RCS_ACCEPT_STATUS_NONE}${RCS_CONVERSATION_STATUS_PENDING}`]:
        CHAT_HEADER_ACCEPT_DECLINE,
      [`${RCS_ACCEPT_STATUS_ACCEPTED}${RCS_CONVERSATION_STATUS_ACTIVE}`]:
        CHAT_HEADER_END,
    };

    if (!rcsConversation || !rcsConversation.data) {
      return null;
    }
    const { accept_status, conversation_status } = rcsConversation.data;
    const buttonType =
      STATUS_TO_BUTTON_MAP[`${accept_status}${conversation_status}`] || null;
    return buttonType;
  }, [rcsConversation]);

  const chatHeight = isCollapsed
    ? `calc(100% - ${RCS_CHAT_HEADER_HEIGHT})`
    : '100%';

  const containerHeight = useMemo(() => {
    // Chat has been started and not collapsed or chat has not been started and
    // the preconfigured messages accordion is shown
    const height =
      (!isCollapsed && !showButtons) || rcsConversation
        ? '100%'
        : `${RCS_CHAT_HEADER_HEIGHT}px`;
    return height;
  }, [rcsConversation, showButtons, isCollapsed]);

  return (
    <RcsChatContainer
      data-name="rcs-chat-container"
      height={chatHeight}
      flex={isCollapsed ? '0 0 auto' : '1 1 auto'}
      maxHeight={containerHeight}
    >
      <RcsChatHeader
        onClickShowAccordion={handleClickShowAccordion}
        showPreconfiguredMessages={showPreconfiguredMessages}
        showButtons={showButtons}
      />
      {hasTalkJSImprovements ? (
        <RcsChatNext
          callerID={selectedCallerID}
          showButtons={!!showButtons}
          isRcsChatBoxVisible={!isCollapsed}
        />
      ) : (
        <RcsChat
          callerID={selectedCallerID}
          showPreconfiguredMessages={showPreconfiguredMessages}
          showButtons={!!showButtons}
          isRcsChatBoxVisible={!isCollapsed}
        />
      )}
      {!isCollapsed && showButtons && <OutgoingTranslationWarning />}
    </RcsChatContainer>
  );
};

export default RcsChatBox;
